import {Inject, Injectable, InjectionToken} from '@angular/core';
import {Observable} from 'rxjs';
import {HttpClient, HttpHeaders, HttpParams, HttpResponse} from '@angular/common/http';
import {map} from 'rxjs/operators';

export const REST_BASE_URL = new InjectionToken('ApiBaseUrl');

export interface BasePage {
  page: number;
  pageCount: number;
  perPage: number;
  totalCount: number;
  totalPages: number;
}

export class Page<T> implements BasePage {
  page: number;
  pageCount: number;
  perPage: number;
  totalCount: number;
  totalPages: number;
  items: Array<T>;
  private headers;
  constructor(items: Array<T>, headers: HttpHeaders) {
    this.items = items;
    this.parseHeaders(headers);
  }

  parseHeaders(headers: HttpHeaders) {
    const keys = headers.keys();
    this.headers = {};
    keys.map(key => {
      this.headers[key] = headers.get(key);
    });
    this.totalCount = parseInt(this.headers['x-pagination-total-count'],  10);
    this.page       = parseInt(this.headers['x-pagination-current-page'], 10) || null;
    this.totalPages = parseInt(this.headers['x-pagination-page-count'],   10) || 1;
    this.perPage    = parseInt(this.headers['x-pagination-per-page'],     10) || null;
  }
}

export interface ToHttpParams {
  buildHttpParams(httpParams: HttpParams): HttpParams;
}

export class Sort implements ToHttpParams {

  // httpParamName = 'sort';

  sort = [];

  buildHttpParams(httpParams: HttpParams) {
    return httpParams.append(this.httpParamName, this.sort.join(','));
  }

  constructor(sort?: string, public httpParamName = 'sort') {
    if (sort && sort.length) {
      this.sort = sort.split(',');
    }
  }

  setSort(sort: string) {
    this.sort = sort.split(',');
  }

  // add single column sort replace old
  addSort(sort: string) {
  }
}

export class Filter implements ToHttpParams {
  attributes: Array<FilterField> = [];

  constructor(public formName: string, filter?: {[name: string]: any}) {
    for (const name in filter) {
      if (filter.hasOwnProperty(name) && filter[name] !== null) {
        this.attributes.push(
          new FilterField(
            name,
            Array.isArray(filter[name]) ? filter[name].map(v => v.toString()) : filter[name].toString())
        );
      }
    }
  }

  buildHttpParams(httpParams: HttpParams) {
    this.attributes.forEach(
      param => {
        if (Array.isArray(param.value)) {
          param.value.forEach(
            value => httpParams = httpParams.append(this.formName + '[' + param.name + '][]', value.toString())
          );
        } else {
          httpParams = httpParams.append(this.formName + '[' + param.name + ']', param.value.toString());
        }
      });
    return httpParams;
  }

  resetAll(): Filter {
    this.attributes = [];
    return this;
  }

  reset(name: string): Filter {
    this.attributes = this.attributes.filter(field => field.name !== name);
    return this;
  }

  set(name: string, value: any, replace = true): Filter {
    if (replace) {
      this.reset(name);
    }
    this.attributes.push(new FilterField(name, value));
    return this;
  }
}

export class FilterField {
  // name: string;
  // value: string;
  constructor(public name: string, public value: string | string[]) {}
}

export class Pagination implements ToHttpParams {
  constructor(public page: number = 1, public perPage: number = 25) {
  }

  buildHttpParams(httpParams: HttpParams): HttpParams {
    httpParams = this.page    ? httpParams.append('page',     this.page.toString(10))    : httpParams;
    httpParams = this.perPage ? httpParams.append('per-page', this.perPage.toString(10)) : httpParams;
    return httpParams;
  }

}

@Injectable()
export class RestService {

  constructor(
    public http: HttpClient,
    @Inject(REST_BASE_URL) public baseUrl: string
  ) {}

  list<T>(
    resourceName,
    pagination?: Pagination,
    filter?: Filter,
    sort?: Sort,
    fields: string = '',
    expand: string = ''
  ): Observable<Page<T>> {
    let httpParams = new HttpParams();
    httpParams = pagination ? pagination.buildHttpParams(httpParams) : httpParams;
    httpParams = filter ? filter.buildHttpParams(httpParams) : httpParams;
    httpParams = sort ? sort.buildHttpParams(httpParams) : httpParams;
    httpParams = fields.length ? httpParams.append('fields', fields) : httpParams;
    httpParams = expand.length ? httpParams.append('expand', expand) : httpParams;
    return this.http
      .get<T[]>(this.baseUrl + resourceName, {observe: 'response', params: httpParams})
      .pipe(
        map(response => {
          return new Page(response.body, response.headers);
        })
      );
  }

  view<T>(resourceName: string, id: number, httpParams?: HttpParams): Observable<HttpResponse<T>> {
    return this.http
      .get<T>(this.baseUrl + resourceName + '/' + id.toString(10), {observe: 'response', params: httpParams});
  }

  create<T>(resourceName: string, model: any) {
    return this.http
      .post<T>(this.baseUrl + resourceName, model, {observe: 'body'});
  }

  update(resourceName: string, id: number, model: any, httpParams?: HttpParams) {
    return this.http
      .put(this.baseUrl + resourceName + '/' + id, model, {observe: 'body', params: httpParams});
  }

}
